import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';

import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { useIdentity } from '@internal/shared';

import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import BookIcon from '@material-ui/icons/Book';
import CardTravel from '@material-ui/icons/CardTravel';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import SettingsSystemDaydreamSharp from '@material-ui/icons/SettingsSystemDaydreamSharp';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import UsersIcon from '@material-ui/icons/Group';
import { QuestionMarkIcon } from '../icons/QuestionMarkIcon';
import { ApiIcon } from '../icons/ApiIcon';
import { MonitoringIcon } from '../icons/MonitoringIcon';

// Local custom icons
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  dxPortalAdvancedFeaturesPermission,
  dxPortalBasicFeaturesPermission,
} from '@internal/shared/src/permission/opa_permissions';
import { PermissionWrapper } from './PermissionWrapper';
import { useHasAnyUserRequestPermission } from '../userRequests/hooks';
import { OpenFeedbackModal } from '@parsifal-m/backstage-plugin-open-feedback';
import { ConfigApi, configApiRef, useApi } from '@backstage/core-plugin-api';
import { UserRequestsSidebarItem } from './UserRequestsSidebarItem';

const useSidebarStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
  divider: {
    backgroundColor: 'rgba(255,255,255, 0.2)',
  },
  settings: {
    marginBottom: 10,
  },
});

const UserRequestsGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { hasPermission, isLoading } = useHasAnyUserRequestPermission();
  if (isLoading || !hasPermission) return null;
  return <>{children}</>;
};

const SidebarLogo = () => {
  const classes = useSidebarStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

type RootProps = PropsWithChildren<{
  onOpenLogin?: () => void;
}>;

export const Root = ({ children, onOpenLogin }: RootProps) => {
  const classes = useSidebarStyles();
  const { idToken } = useIdentity();
  const isUserLoggedIn = Boolean(idToken);
  const openLoginDialog = onOpenLogin ?? (() => {});
  const configApi: ConfigApi = useApi(configApiRef);
  const dxmGrafanaUrl = configApi.getOptionalString('dxmGrafana.url') || '/404';
  const docsPageRoute = 'docs-v2';
  const faqPageRoute = '/docs-v2/dxm/help';

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        {!isUserLoggedIn ? (
          <>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarDivider className={classes.divider} />
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <SidebarItem
                icon={ApiIcon}
                text="APIs"
                disableHighlight
                onClick={openLoginDialog}
              />
              <SidebarItem
                icon={LibraryBooks}
                text="Docs"
                disableHighlight
                onClick={openLoginDialog}
              />
            </SidebarGroup>
            <SidebarSpace />
            <SidebarDivider className={classes.divider} />
            <SidebarGroup
              label="Support"
              icon={<UserSettingsSignInAvatar />}
              to="/contact-us"
            >
              <SidebarItem
                icon={ContactSupportIcon}
                to="/contact-us"
                text="Contact Us"
              />
            </SidebarGroup>
          </>
        ) : (
          <>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarDivider className={classes.divider} />
              <SidebarItem icon={HomeIcon} to="/" text="Home" />

              <SidebarItem icon={ApiIcon} to="api-docs" text="APIs" />
              <SidebarItem icon={LibraryBooks} to={docsPageRoute} text="Docs" />

              <PermissionWrapper permission={dxPortalBasicFeaturesPermission}>
                <SidebarItem
                  icon={SettingsSystemDaydreamSharp}
                  to="system-status"
                  text="System Status"
                />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem icon={CardTravel} to="toolbox" text="ToolBox" />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={CreateComponentIcon}
                  to="create"
                  text="Intake Forms"
                />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem icon={BookIcon} to="storybook" text="Storybook" />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={MonitoringIcon}
                  to={dxmGrafanaUrl}
                  text="Monitoring"
                  target="_blank"
                />
              </PermissionWrapper>

              <SidebarItem
                icon={QuestionMarkIcon}
                to={faqPageRoute}
                text="FAQ"
              />

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={ThumbUpIcon}
                  to="open-feedback"
                  text="Reviews"
                />
              </PermissionWrapper>

              <SidebarSpace />

              <SidebarGroup>
                <SidebarDivider className={classes.divider} />
                <PermissionWrapper
                  permission={dxPortalAdvancedFeaturesPermission}
                >
                  <UserRequestsGuard>
                    <UserRequestsSidebarItem />
                  </UserRequestsGuard>
                </PermissionWrapper>

                <PermissionWrapper
                  permission={dxPortalAdvancedFeaturesPermission}
                >
                  <SidebarItem
                    icon={UsersIcon}
                    to="user-management"
                    text="Users"
                  />
                </PermissionWrapper>

                <OpenFeedbackModal color="primary" />

                <SidebarItem
                  icon={ContactSupportIcon}
                  to="contact-us"
                  text="Contact Us"
                />

                <SidebarItem
                  icon={SettingsIcon}
                  to="settings"
                  className={classes.settings}
                  text="Settings"
                />
              </SidebarGroup>
            </SidebarGroup>
          </>
        )}
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
